import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import HistoryIcon from '@material-ui/icons/History';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import SettingsIcon from '@material-ui/icons/Settings';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { saveDataType } from '@actions/data-types-actions';
import { pushApplicationMessage } from '@actions/messages-actions';
import { fetchDataDetail } from '@actions/data-detail-actions';
import * as colors from '@constants/colors';
import './top-bar-action-entity-type-icons.scss';

const TopBarActionEntityTypeIcons = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const { data } = useSelector(state => state.dataDetail);

  const getParam = useCallback(name => new URLSearchParams(location.search).get(name), [location.search]);
  const isPreview = getParam('mode') === 'preview';
  const isSettingsOpen = getParam('panel') === 'settings';

  const setUrlParam = useCallback((param, value) => {
    const params = new URLSearchParams(location.search);
    if (value) {
      params.set(param, value);
    } else {
      params.delete(param);
    }
    const search = params.toString();
    history.replace(search ? `${location.pathname}?${search}` : location.pathname);
  }, [history, location]);

  const handleSaveDraft = useCallback(() => {
    if (!data) {
      return;
    }
    dispatch(saveDataType('map_type', id, { is_public: false }, () => {
      dispatch(fetchDataDetail('map_type', id));
      dispatch(pushApplicationMessage('Draft saved.'));
    }));
  }, [dispatch, id, data]);

  const handlePublish = useCallback(() => {
    if (!data) {
      return;
    }
    dispatch(saveDataType('map_type', id, { is_public: true }, () => {
      dispatch(fetchDataDetail('map_type', id));
      dispatch(pushApplicationMessage('Published.'));
    }));
  }, [dispatch, id, data]);

  const handlePreview = useCallback(() => {
    if (isPreview) {
      setUrlParam('mode', null);
    } else {
      setUrlParam('mode', 'preview');
    }
  }, [isPreview, setUrlParam]);

  const handleSettings = useCallback(() => {
    if (isSettingsOpen) {
      setUrlParam('panel', null);
    } else {
      setUrlParam('panel', 'settings');
    }
  }, [isSettingsOpen, setUrlParam]);

  if (!data) {
    return null;
  }

  return (
    <div styleName="entity-type-icons-container">
      <Chip
        label={data.is_public ? 'Published' : 'Draft'}
        size="small"
        style={{
          backgroundColor: data.is_public ? '#E8F5E9' : '#F5F5F5',
          color: data.is_public ? '#2E7D32' : '#757575',
          fontWeight: 500,
          fontSize: '0.75rem',
          marginRight: '0.75rem'
        }}
      />
      <Button
        size="small"
        variant="outlined"
        onClick={handleSaveDraft}
        styleName="action-btn"
      >
        Save Draft
      </Button>
      <Button
        color="primary"
        size="small"
        variant="contained"
        onClick={handlePublish}
        styleName="action-btn action-btn-publish"
      >
        Publish
      </Button>
      <Tooltip title="Preview">
        <IconButton size="small" onClick={handlePreview}>
          <VisibilityIcon
            fontSize="small"
            htmlColor={isPreview ? colors.dotmapsBlue : colors.dotmapsGray}
          />
        </IconButton>
      </Tooltip>
      <Tooltip title="Version history">
        <IconButton size="small">
          <HistoryIcon fontSize="small" htmlColor={colors.dotmapsGray} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Settings">
        <IconButton size="small" onClick={handleSettings}>
          <SettingsIcon
            fontSize="small"
            htmlColor={isSettingsOpen ? colors.dotmapsBlue : colors.dotmapsGray}
          />
        </IconButton>
      </Tooltip>
      <Tooltip title="More">
        <IconButton size="small">
          <MoreVertIcon fontSize="small" htmlColor={colors.dotmapsGray} />
        </IconButton>
      </Tooltip>
    </div>
  );
};

export default TopBarActionEntityTypeIcons;
